import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import {
  getForecastProjection, getForecastExceptions, getForecastAccuracy,
  getSuggestedReorders, getForecastShortages, getForecastOverstock,
  listAdjustments, approveForecastAdjustment, rejectForecastAdjustment,
} from '../lib/api';
import SkuForecastDrawer from '../components/SkuForecastDrawer';
import PlannerCalendar from '../components/PlannerCalendar';
import WhatIfSimulator from '../components/WhatIfSimulator';
import {
  ChartLineUp, Warning, Package, TrendDown, TrendUp, Target,
  ArrowsClockwise, Clock, Fire, Snowflake, Timer, ArrowRight,
  Gauge, Calculator, ShoppingCart,
  CalendarBlank, Lightning,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';


const TABS = [
  { id: 'dashboard',  label: 'Dashboard',          icon: Gauge },
  { id: 'projection', label: 'Projection Engine',  icon: Calculator },
  { id: 'calendar',   label: 'Calendar',           icon: CalendarBlank },
  { id: 'whatif',     label: 'What-If',            icon: Lightning },
  { id: 'exceptions', label: 'Exception Alerts',   icon: Warning },
  { id: 'shortages',  label: 'Shortage Report',    icon: TrendDown },
  { id: 'overstock',  label: 'Overstock Report',   icon: TrendUp },
  { id: 'reorders',   label: 'Suggested Reorders', icon: ShoppingCart },
  { id: 'accuracy',     label: 'Forecast Accuracy',  icon: Target },
  { id: 'adjustments',  label: 'Adjustments',        icon: Clock },
];


const EXCEPTION_META = {
  stockout_risk:     { icon: Fire,       color: 'text-red-600 bg-red-500/10',     label: 'Stockout Risk' },
  overstock_risk:    { icon: Snowflake,  color: 'text-blue-600 bg-blue-500/10',   label: 'Overstock Risk' },
  late_inbound_po:   { icon: Timer,      color: 'text-amber-600 bg-amber-500/10', label: 'Late Inbound PO' },
  demand_spike:      { icon: TrendUp,    color: 'text-purple-600 bg-purple-500/10', label: 'Demand Spike' },
  slow_moving:       { icon: Package,    color: 'text-muted-foreground bg-muted0/10', label: 'Slow Moving' },
  expiring_stock:    { icon: Clock,      color: 'text-orange-600 bg-orange-500/10', label: 'Expiring Stock' },
  forecast_variance: { icon: Target,     color: 'text-pink-600 bg-pink-500/10',   label: 'Forecast Variance' },
};


function Stat({ label, value, icon: Icon, hint, tone = 'default', testid }) {
  const tones = {
    default: 'border-border',
    success: 'border-emerald-500/40',
    warn: 'border-amber-500/40',
    danger: 'border-red-500/40',
    info: 'border-blue-500/40',
  };
  return (
    <Card className={`p-5 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider opacity-60 font-mono">{label}</div>
          <div className="text-3xl font-bold tracking-tight mt-1">{value}</div>
          {hint && <div className="text-[12px] opacity-70 mt-1">{hint}</div>}
        </div>
        {Icon && <Icon className="size-6 opacity-40" weight="duotone" />}
      </div>
    </Card>
  );
}


// -------------------------------------------------------------------------- //
// Dashboard tab — pulls accuracy + exceptions summary + shortages
// -------------------------------------------------------------------------- //
function DashboardTab({ onJumpTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [acc, ex, sh, ov] = await Promise.all([
          getForecastAccuracy({ lookback_days: 30 }),
          getForecastExceptions(),
          getForecastShortages(),
          getForecastOverstock({ min_days_cover: 90 }),
        ]);
        setData({ accuracy: acc.data, exceptions: ex.data, shortages: sh.data, overstock: ov.data });
      } catch (e) { console.error(e); toast.error('Failed to load dashboard'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-sm opacity-60">Loading dashboard…</div>;
  if (!data) return <div className="text-sm opacity-60">No data</div>;

  const { accuracy, exceptions, shortages, overstock } = data;

  return (
    <div className="space-y-6" data-testid="planning-dashboard">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Stat label="Forecast Accuracy" value={`${accuracy.accuracy_pct}%`}
              icon={Target} hint={`MAPE ${accuracy.mape_pct}% · ${accuracy.sample_size_skus} SKUs`}
              tone={accuracy.accuracy_pct > 70 ? 'success' : accuracy.accuracy_pct > 40 ? 'warn' : 'danger'}
              testid="stat-accuracy" />
        <Stat label="Shortage SKUs" value={shortages.total}
              icon={TrendDown} hint="At risk of stockout" tone={shortages.total > 0 ? 'danger' : 'success'}
              testid="stat-shortages" />
        <Stat label="Overstock SKUs" value={overstock.total}
              icon={TrendUp} hint={`$${overstock.total_excess_value.toLocaleString()} tied up`}
              tone={overstock.total > 10 ? 'warn' : 'default'}
              testid="stat-overstock" />
        <Stat label="Open Exceptions" value={exceptions.total}
              icon={Warning} hint="All 7 alert types" tone={exceptions.total > 50 ? 'danger' : exceptions.total > 10 ? 'warn' : 'default'}
              testid="stat-exceptions" />
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold tracking-tight">Exception breakdown</h3>
            <p className="text-[12px] opacity-70">By alert type — click to drill into the Exceptions tab</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => onJumpTo('exceptions')}>
            View all
            <ArrowRight className="size-3.5 ml-1" />
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
          {Object.entries(exceptions.by_type || {}).map(([t, count]) => {
            const meta = EXCEPTION_META[t] || { label: t, icon: Warning, color: 'text-muted-foreground bg-muted0/10' };
            const Icon = meta.icon;
            return (
              <div key={t}
                   className={`p-3 rounded border cursor-pointer hover:scale-[1.02] transition-transform ${count > 0 ? meta.color + ' border-current/30' : 'opacity-50 border-border'}`}
                   onClick={() => onJumpTo('exceptions')}
                   data-testid={`exception-tile-${t}`}>
                <Icon className="size-4 mb-1.5" />
                <div className="text-[11px] font-semibold">{meta.label}</div>
                <div className="text-xl font-bold mt-0.5">{count}</div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-center gap-6 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-wider opacity-60">Forecast Bias</div>
            <div className="text-2xl font-bold mt-0.5">
              {accuracy.bias_direction}
              <span className="text-[13px] font-mono opacity-70 ml-2">
                {accuracy.bias_units > 0 ? '+' : ''}{accuracy.bias_units} units/SKU
              </span>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider opacity-60">Inventory Turns</div>
            <div className="text-2xl font-bold mt-0.5">{accuracy.inventory_turns_annualized}x
              <span className="text-[13px] font-mono opacity-70 ml-2">per year</span>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider opacity-60">Stockout Days</div>
            <div className="text-2xl font-bold mt-0.5">{accuracy.stockout_sku_count}
              <span className="text-[13px] font-mono opacity-70 ml-2">of {accuracy.total_sku_count} SKUs at 0</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}


// -------------------------------------------------------------------------- //
// Projection tab — SKU × Week time-phased matrix
// -------------------------------------------------------------------------- //
function ProjectionTab({ onOpenSku, adjustmentsVersion }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [horizon, setHorizon] = useState(8);
  const [limit, setLimit] = useState(25);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getForecastProjection({ horizon_weeks: horizon, limit });
      setData(res.data);
    } catch (e) { toast.error('Projection failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [horizon, limit, adjustmentsVersion]);

  if (loading) return <div className="text-sm opacity-60">Computing projection…</div>;
  if (!data) return null;

  const weekHeaders = data.rows[0]?.weeks || [];
  const riskPill = {
    stockout:  'bg-red-500/15 text-red-600',
    overstock: 'bg-blue-500/15 text-blue-600',
    declining: 'bg-amber-500/15 text-amber-600',
    healthy:   'bg-emerald-500/15 text-emerald-600',
  };

  return (
    <div className="space-y-4" data-testid="planning-projection">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-[12px] opacity-70">Horizon:</label>
        <select value={horizon} onChange={(e) => setHorizon(parseInt(e.target.value, 10))}
                className="h-8 px-2 text-[12px] bg-background border border-border rounded font-mono"
                data-testid="projection-horizon-select">
          {[4, 6, 8, 12, 16, 26].map(w => <option key={w} value={w}>{w} weeks</option>)}
        </select>
        <label className="text-[12px] opacity-70 ml-3">SKU limit:</label>
        <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))}
                className="h-8 px-2 text-[12px] bg-background border border-border rounded font-mono">
          {[25, 50, 100, 250, 500].map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <Button size="sm" variant="outline" onClick={load}><ArrowsClockwise className="size-3.5" /> Refresh</Button>
        <div className="ml-auto text-[11px] opacity-60 font-mono">
          {data.count} SKUs · horizon {data.horizon_weeks}w · <span className="opacity-80">click row to drill down</span>
        </div>
      </div>

      <Card className="overflow-auto max-h-[70vh]">
        <table className="w-full text-[12px]" data-testid="projection-table">
          <thead className="bg-muted/40 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 sticky left-0 bg-muted/40">SKU</th>
              <th className="text-left px-3 py-2">Risk</th>
              <th className="text-right px-3 py-2">Current</th>
              <th className="text-right px-3 py-2">Reserved</th>
              {weekHeaders.map((w, i) => (
                <th key={i} className="text-right px-3 py-2 whitespace-nowrap">W{i + 1}<br /><span className="text-[9px] opacity-60 font-mono">{w.week_start}</span></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.sku_id}
                  onClick={() => onOpenSku?.(r)}
                  className="border-t border-border/40 hover:bg-muted/30 cursor-pointer"
                  data-testid={`projection-row-${r.sku_code}`}>
                <td className="px-3 py-2 font-mono sticky left-0 bg-background group-hover:bg-muted/30">{r.sku_code}</td>
                <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${riskPill[r.risk] || ''}`}>{r.risk}</span></td>
                <td className="px-3 py-2 text-right font-mono">{r.current_stock}</td>
                <td className="px-3 py-2 text-right font-mono opacity-60">-{r.reserved || 0}</td>
                {r.weeks.map((w, i) => (
                  <td key={i} className={`px-3 py-2 text-right font-mono ${w.closing < 0 ? 'text-red-600 font-bold' : w.closing < r.current_stock * 0.25 ? 'text-amber-600' : ''}`}>
                    {w.closing.toFixed(0)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}


// -------------------------------------------------------------------------- //
// Adjustments tab — pending approvals + full history
// -------------------------------------------------------------------------- //
function AdjustmentsTab({ adjustmentsVersion, onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  const load = async () => {
    setLoading(true);
    try {
      const r = await listAdjustments({
        status: filter || undefined,
        limit: 200,
      });
      setRows(r.data.rows || []);
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, adjustmentsVersion]);

  const handleApprove = async (id) => {
    try {
      await approveForecastAdjustment(id);
      toast.success('Approved');
      onChange?.();
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Reason for rejection?');
    if (!reason?.trim()) return;
    try {
      await rejectForecastAdjustment(id, reason.trim());
      toast.success('Rejected');
      onChange?.();
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };

  return (
    <div className="space-y-4" data-testid="planning-adjustments">
      <div className="flex items-center gap-2">
        {['pending', 'approved', 'rejected', ''].map((f) => (
          <Button key={f || 'all'} size="sm"
                  variant={filter === f ? 'default' : 'outline'}
                  onClick={() => setFilter(f)}
                  data-testid={`adj-filter-${f || 'all'}`}>
            {f || 'All'}
          </Button>
        ))}
        <span className="ml-auto text-[11px] opacity-60 font-mono">{rows.length} adjustment{rows.length === 1 ? '' : 's'}</span>
      </div>

      <Card className="overflow-auto max-h-[70vh]">
        {loading ? (
          <div className="p-4 text-sm opacity-60">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm opacity-60">
            {filter === 'pending' ? 'No pending approvals. You\'re all caught up 🎉' : 'No adjustments'}
          </div>
        ) : (
          <table className="w-full text-[12px]" data-testid="adjustments-table">
            <thead className="bg-muted/40 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">SKU</th>
                <th className="text-left px-3 py-2">Week</th>
                <th className="text-right px-3 py-2">Delta</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="border-t border-border/40">
                  <td className="px-3 py-2 font-mono">{a.sku_id?.slice(-6)}</td>
                  <td className="px-3 py-2 font-mono">wk {a.week_offset}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    <span className={a.delta > 0 ? 'text-emerald-600' : 'text-red-600'}>
                      {a.delta > 0 ? '+' : ''}{a.delta}
                    </span>
                  </td>
                  <td className="px-3 py-2 opacity-80">{a.reason}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
                      a.status === 'approved' ? 'bg-emerald-500/20 text-emerald-700' :
                      a.status === 'rejected' ? 'bg-red-500/20 text-red-700' :
                      'bg-amber-500/20 text-amber-700'
                    }`}>{a.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {a.status === 'pending' && (
                      <div className="flex gap-1.5 justify-end">
                        <Button size="sm" variant="default" className="h-7 px-2 text-[11px]"
                                onClick={() => handleApprove(a.id)}
                                data-testid={`adj-approve-${a.id}`}>Approve</Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                                onClick={() => handleReject(a.id)}>Reject</Button>
                      </div>
                    )}
                    {a.reject_reason && (
                      <div className="text-[10px] opacity-60 italic">{a.reject_reason}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}


// -------------------------------------------------------------------------- //
// Exceptions tab
// -------------------------------------------------------------------------- //
function ExceptionsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const r = await getForecastExceptions(); setData(r.data); }
      catch (e) { toast.error('Failed'); } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(
    () => typeFilter ? (data?.alerts || []).filter(a => a.type === typeFilter) : (data?.alerts || []),
    [data, typeFilter],
  );

  if (loading) return <div className="text-sm opacity-60">Loading exceptions…</div>;
  if (!data) return null;

  const sevPill = { high: 'bg-red-500/20 text-red-700', medium: 'bg-amber-500/20 text-amber-700', low: 'bg-muted0/20 text-foreground' };

  return (
    <div className="space-y-4" data-testid="planning-exceptions">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant={typeFilter === '' ? 'default' : 'outline'} onClick={() => setTypeFilter('')}>
          All ({data.total})
        </Button>
        {Object.entries(data.by_type).map(([t, c]) => (
          <Button key={t} size="sm" variant={typeFilter === t ? 'default' : 'outline'}
                  disabled={c === 0} onClick={() => setTypeFilter(t)}
                  data-testid={`exception-filter-${t}`}>
            {EXCEPTION_META[t]?.label || t} ({c})
          </Button>
        ))}
      </div>

      <Card className="overflow-auto max-h-[70vh]">
        <table className="w-full text-[13px]" data-testid="exceptions-table">
          <thead className="bg-muted/40 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Severity</th>
              <th className="text-left px-3 py-2">Detail</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={3} className="px-3 py-8 text-center opacity-60">No exceptions in this category</td></tr>
            ) : filtered.map((a, i) => {
              const meta = EXCEPTION_META[a.type] || { label: a.type, icon: Warning };
              const Icon = meta.icon;
              return (
                <tr key={i} className="border-t border-border/40">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Icon className="size-4" weight="duotone" />
                      <span className="font-mono text-[11px]">{meta.label}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${sevPill[a.severity] || ''}`}>{a.severity}</span>
                  </td>
                  <td className="px-3 py-2">{a.message}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}


// -------------------------------------------------------------------------- //
// Shortages / Overstock / Reorders (shared table-ish)
// -------------------------------------------------------------------------- //
function ListTab({ fetcher, columns, totalsKey, testid, emptyMsg }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const r = await fetcher(); setData(r.data); }
      catch (e) { toast.error('Load failed'); } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  if (loading) return <div className="text-sm opacity-60">Loading…</div>;
  if (!data) return null;
  const rows = data.rows || [];

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex items-center gap-4 flex-wrap text-[12px]">
        <span className="font-mono">{data.total} SKUs</span>
        {totalsKey && data[totalsKey] !== undefined && (
          <span className="font-mono opacity-70">
            Total: ${Number(data[totalsKey]).toLocaleString()}
          </span>
        )}
      </div>
      <Card className="overflow-auto max-h-[70vh]">
        <table className="w-full text-[12px]">
          <thead className="bg-muted/40 sticky top-0">
            <tr>{columns.map((c) => <th key={c.key} className={`px-3 py-2 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length} className="text-center py-8 opacity-60">{emptyMsg || 'Nothing to show'}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-t border-border/40">
                {columns.map((c) => (
                  <td key={c.key} className={`px-3 py-2 ${c.align === 'right' ? 'text-right font-mono' : ''}`}>
                    {c.render ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}


// -------------------------------------------------------------------------- //
// Accuracy tab
// -------------------------------------------------------------------------- //
function AccuracyTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lookback, setLookback] = useState(30);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const r = await getForecastAccuracy({ lookback_days: lookback }); setData(r.data); }
      catch (e) { toast.error('Load failed'); } finally { setLoading(false); }
    })();
  }, [lookback]);

  if (loading) return <div className="text-sm opacity-60">Computing accuracy…</div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="planning-accuracy">
      <div className="flex items-center gap-3">
        <label className="text-[12px] opacity-70">Lookback:</label>
        <select value={lookback} onChange={(e) => setLookback(parseInt(e.target.value, 10))}
                className="h-8 px-2 text-[12px] bg-background border border-border rounded font-mono">
          {[7, 14, 30, 60, 90, 180].map(d => <option key={d} value={d}>{d} days</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Stat label="Forecast Accuracy" value={`${data.accuracy_pct}%`} icon={Target}
              hint={`MAPE ${data.mape_pct}%`} testid="accuracy-pct"
              tone={data.accuracy_pct > 70 ? 'success' : data.accuracy_pct > 40 ? 'warn' : 'danger'} />
        <Stat label="Bias" value={`${data.bias_units > 0 ? '+' : ''}${data.bias_units}`}
              hint={`${data.bias_direction} · units/SKU`} icon={TrendUp} />
        <Stat label="Inventory Turns" value={`${data.inventory_turns_annualized}x`}
              hint="Annualized" icon={ArrowsClockwise} tone="info" />
        <Stat label="Stockout SKUs" value={data.stockout_sku_count}
              hint={`of ${data.total_sku_count} total`} icon={Warning}
              tone={data.stockout_sku_count > 50 ? 'danger' : 'warn'} />
      </div>

      <Card className="p-5 text-[13px] space-y-3">
        <h3 className="font-bold">How accuracy is computed</h3>
        <p className="opacity-80">
          For each SKU we compare the actual consumption over the last 7 days to the forecast the
          model would have produced using only data from the window <span className="font-mono">[now−14d .. now−7d]</span>.
          MAPE = average of <span className="font-mono">|actual − forecast| / max(actual, forecast, 1)</span>.
          Accuracy = <span className="font-mono">100% − MAPE</span>.
        </p>
        <p className="opacity-80">
          Bias shows if the model is <b>over-forecasting</b> (positive) or <b>under-forecasting</b> (negative) on average.
          Inventory turns = outbound movement / avg inventory, annualised.
        </p>
        <div className="mt-2 pt-3 border-t border-border/40 flex gap-6 text-[12px] font-mono">
          <span>Sample size: {data.sample_size_skus} SKUs</span>
          <span>Total actual 7d: {data.total_actual_7d}</span>
          <span>Total forecast 7d: {data.total_forecast_7d}</span>
        </div>
      </Card>
    </div>
  );
}


// -------------------------------------------------------------------------- //
// Page
// -------------------------------------------------------------------------- //
export default function MasterPlanning() {
  const [tab, setTab] = useState('dashboard');
  const [drawerSku, setDrawerSku] = useState(null);
  // Bumping this triggers a re-fetch of projection + adjustments tabs after
  // any create/approve/reject so the UI stays consistent.
  const [adjustmentsVersion, setAdjustmentsVersion] = useState(0);
  const bumpAdjustments = () => setAdjustmentsVersion((v) => v + 1);

  const reorderCols = [
    { key: 'sku_code', label: 'SKU' },
    { key: 'sku_name', label: 'Name' },
    { key: 'current_stock', label: 'Current', align: 'right' },
    { key: 'days_of_cover', label: 'Days Cover', align: 'right' },
    { key: 'suggested_qty', label: 'Suggested Qty', align: 'right' },
    { key: 'estimated_cost', label: 'Est. Cost', align: 'right', render: (r) => `$${Number(r.estimated_cost).toLocaleString()}` },
  ];

  const shortageCols = [
    { key: 'sku_code', label: 'SKU' },
    { key: 'sku_name', label: 'Name' },
    { key: 'current_stock', label: 'Current', align: 'right' },
    { key: 'days_of_cover', label: 'Days Cover', align: 'right' },
    { key: 'risk', label: 'Risk', render: (r) => (
      <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${r.risk === 'critical' ? 'bg-red-500/20 text-red-700' : 'bg-amber-500/20 text-amber-700'}`}>{r.risk}</span>
    ) },
    { key: 'suggested_qty', label: 'Reorder Qty', align: 'right' },
  ];

  const overstockCols = [
    { key: 'sku_code', label: 'SKU' },
    { key: 'sku_name', label: 'Name' },
    { key: 'current_stock', label: 'Current', align: 'right' },
    { key: 'days_of_cover', label: 'Days Cover', align: 'right', render: (r) => (
      <span className={r.days_of_cover > 180 ? 'text-red-600 font-bold' : 'text-amber-600'}>{r.days_of_cover}d</span>
    ) },
    { key: 'excess_units', label: 'Excess Units', align: 'right' },
    { key: 'excess_value', label: 'Excess Value', align: 'right', render: (r) => `$${Number(r.excess_value).toLocaleString()}` },
  ];

  return (
    <Layout>
      <div className="max-w-[1600px] mx-auto px-6 py-8 space-y-6" data-testid="master-planning-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Master Planning</h1>
          <p className="mt-2 text-sm opacity-70 max-w-3xl">
            Demand forecast, inventory projection, exception alerts, and reorder suggestions —
            everything a planner needs to manage by exception.
          </p>
        </div>

        <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`planning-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2.5 text-[13px] tracking-tight border-b-2 -mb-px whitespace-nowrap flex items-center gap-2 transition-colors
                  ${tab === t.id ? 'border-primary font-bold' : 'border-transparent opacity-60 hover:opacity-100'}`}
              >
                <Icon className="size-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div>
          {tab === 'dashboard'  && <DashboardTab onJumpTo={setTab} />}
          {tab === 'projection' && <ProjectionTab onOpenSku={setDrawerSku} adjustmentsVersion={adjustmentsVersion} />}
          {tab === 'calendar'   && <PlannerCalendar />}
          {tab === 'whatif'     && <WhatIfSimulator />}
          {tab === 'exceptions' && <ExceptionsTab />}
          {tab === 'shortages' && (
            <ListTab
              fetcher={() => getForecastShortages()}
              columns={shortageCols}
              testid="planning-shortages"
              emptyMsg="No SKUs at risk — stock levels look healthy 🎉"
            />
          )}
          {tab === 'overstock' && (
            <ListTab
              fetcher={() => getForecastOverstock({ min_days_cover: 90 })}
              columns={overstockCols}
              totalsKey="total_excess_value"
              testid="planning-overstock"
              emptyMsg="No overstock — inventory is lean"
            />
          )}
          {tab === 'reorders' && (
            <ListTab
              fetcher={() => getSuggestedReorders({ min_suggested_qty: 1 })}
              columns={reorderCols}
              totalsKey="estimated_total_cost"
              testid="planning-reorders"
              emptyMsg="No reorders needed right now"
            />
          )}
          {tab === 'accuracy' && <AccuracyTab />}
          {tab === 'adjustments' && <AdjustmentsTab adjustmentsVersion={adjustmentsVersion} onChange={bumpAdjustments} />}
        </div>

        {drawerSku && (
          <SkuForecastDrawer
            sku={drawerSku}
            onClose={() => setDrawerSku(null)}
            onAdjustmentChange={bumpAdjustments}
          />
        )}
      </div>
    </Layout>
  );
}
